import type { Doctor } from "@/features/clinic/types/doctor";
import type { Treatment } from "@/features/clinic/types/treatment";
import type { Contact } from "@/features/contact/types";
import type {
  Activity,
  CallDirection,
  CallOutcome,
  CallTranscriptionLine,
} from "@/types/activity";
import type { Channel } from "@/types/channel";
import type { ClosingNote } from "@/types/closing-note";
import type { Conversation } from "@/types/conversation";
import type { Attachment, Message, MessageStatus } from "@/types/message";
import type { User } from "@/types/user";

import {
  createId,
  createPastTimestamp,
  createPhoneNumber,
  pickMultiple,
  pickRandom,
  randomBool,
  randomInt,
} from "./base";
import { makeAgentRunSteps } from "./agent-step";

import { seedMockReplyReview } from "@/mock/handlers/reply-review";

interface ConversationFlow {
  name: string;
  treatmentKeywords?: string[]; // To match with actual treatment
  doctorSpecialization?: string; // To match with doctor
  messages: {
    text: string;
    owner: "contact" | "user";
    delayMinutes: number; // Time after previous message
    replyToIndex?: number; // Index of a prior message in this flow this one replies to (quoted)
  }[];
}

/**
 * Call transcription - embedded in call activity payload
 * This represents the transcribed phone conversation
 */
const CALL_TRANSCRIPTION: CallTranscriptionLine[] = [
  {
    speaker: "agent",
    text: "Hello, thank you for calling ACME Clinic. How can I help you today?",
  },
  {
    speaker: "customer",
    text: "Hi, I'm calling to ask about laser hair removal. I saw your ad on Instagram.",
  },
  {
    speaker: "agent",
    text: "Of course! I'd be happy to help. Are you looking at a specific area for treatment?",
  },
  {
    speaker: "customer",
    text: "Yes, I'm interested in full legs. How much does that cost per session?",
  },
  {
    speaker: "agent",
    text: "Full legs laser hair removal is AED 600 per session. We typically recommend 6 to 8 sessions for optimal results, spaced about 4 to 6 weeks apart.",
  },
  {
    speaker: "customer",
    text: "That sounds reasonable. How long does each session take?",
  },
  {
    speaker: "agent",
    text: "Each session takes approximately 45 to 60 minutes. We use the latest Soprano ICE technology which is virtually painless and works on all skin types.",
  },
  {
    speaker: "customer",
    text: "Oh that's great to hear. I've heard laser can be painful.",
  },
  {
    speaker: "agent",
    text: "Not with our technology! Most patients describe it as a warm sensation. Would you like to book a free consultation? Dr. Priya Sharma can assess your skin and create a personalized treatment plan.",
  },
  {
    speaker: "customer",
    text: "Yes, I'd like that. Do you have availability this week?",
  },
  {
    speaker: "agent",
    text: "Let me check... We have Tuesday at 2 PM or Thursday at 11 AM available. Which works better for you?",
  },
  { speaker: "customer", text: "Tuesday at 2 PM works perfectly for me." },
  {
    speaker: "agent",
    text: "Perfect! I've booked you with Dr. Priya Sharma on Tuesday at 2:00 PM. You'll receive a confirmation message shortly. Is there anything else I can help you with?",
  },
  {
    speaker: "customer",
    text: "No, that's all. Thank you so much for your help!",
  },
  {
    speaker: "agent",
    text: "You're welcome! We look forward to seeing you on Tuesday. Have a great day!",
  },
];

/**
 * Complete pre-defined conversation flows for ACME Clinic
 * Each flow represents a realistic patient interaction from start to finish
 */
const CONVERSATION_FLOWS: ConversationFlow[] = [
  // Scenario 1: Laser Hair Removal Inquiry & Booking
  {
    name: "Laser Hair Removal Booking",
    treatmentKeywords: ["Laser Hair Removal", "Full Legs"],
    doctorSpecialization: "Laser",
    messages: [
      {
        text: "Hi! I'm interested in laser hair removal for full legs",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Hello! Great choice. Our laser hair removal for full legs costs AED 600 per session. We typically recommend 6-8 sessions for optimal results.",
        owner: "user",
        delayMinutes: 7,
      },
      {
        text: "How long does each session take?",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "Each session takes approximately 45-60 minutes. Would you like to book a free consultation first?",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Yes please! Do you have availability this week?",
        owner: "contact",
        delayMinutes: 5,
      },
      {
        text: "We have availability on Tuesday at 2 PM or Thursday at 11 AM. Which works better for you?",
        owner: "user",
        delayMinutes: 1,
      },
      {
        text: "Tuesday at 2 PM works perfectly",
        owner: "contact",
        delayMinutes: 7,
      },
      {
        text: "Perfect! I've booked you with Dr. Priya Sharma on Tuesday at 2:00 PM. You'll receive a confirmation message shortly.",
        owner: "user",
        delayMinutes: 1,
      },
      { text: "Thank you so much!", owner: "contact", delayMinutes: 2 },
    ],
  },

  // Scenario 2: Botox Price Inquiry
  {
    name: "Botox Inquiry",
    treatmentKeywords: ["Botox", "forehead"],
    doctorSpecialization: "Aesthetic",
    messages: [
      {
        text: "How much is botox for forehead lines?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "For the forehead area, Botox starts at AED 700 for one area. We also offer a package for 3 areas (forehead, frown lines, crow's feet) for AED 1,200.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "What's the difference between 1 and 3 areas?",
        owner: "contact",
        delayMinutes: 11,
      },
      {
        text: "One area targets just the forehead. Three areas give you a complete upper face refresh - smoothing forehead lines, frown lines between brows, and crow's feet around eyes.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "I'll think about it and get back to you",
        owner: "contact",
        delayMinutes: 7,
      },
      {
        text: "Of course! Take your time. Feel free to book a free consultation with Dr. Amira if you'd like to discuss in person.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 3: Hair Transplant Consultation
  {
    name: "Hair Transplant Consultation",
    treatmentKeywords: ["Hair Transplant", "FUE"],
    doctorSpecialization: "Hair",
    messages: [
      {
        text: "I'm looking into hair transplant options. Can I schedule a consultation?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Absolutely! Our hair transplant consultations are complimentary. Dr. James Anderson specializes in FUE hair transplants. When would you like to come in?",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "What does the consultation involve?",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "Dr. Anderson will examine your scalp, discuss your hair loss pattern, explain the FUE procedure, and provide a personalized treatment plan with pricing. It takes about 30 minutes.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Sounds good. Do you have Saturday availability?",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Yes! We have this Saturday at 10 AM or 3 PM available.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "10 AM works for me", owner: "contact", delayMinutes: 5 },
      {
        text: "Great! You're scheduled for Saturday at 10:00 AM with Dr. Anderson. See you then!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 4: Post-Treatment Follow-up
  {
    name: "Carbon Laser Follow-up",
    treatmentKeywords: ["Carbon Laser"],
    messages: [
      {
        text: "I had the Carbon Laser treatment yesterday. My skin is a bit red, is that normal?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes, that's completely normal! Mild redness after Carbon Laser typically lasts 24-48 hours. Make sure to apply the moisturizer we gave you and avoid direct sun exposure.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Okay good! When can I see the results?",
        owner: "contact",
        delayMinutes: 4,
      },
      {
        text: "You'll notice brighter, smoother skin within 3-5 days as the redness fades. Full results appear after about a week.",
        owner: "user",
        delayMinutes: 1,
      },
      {
        text: "Perfect, thank you! The treatment was great btw",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "So glad to hear that! Let us know if you need anything else 😊",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 5: Men's P-Shot Inquiry
  {
    name: "P-Shot Inquiry",
    treatmentKeywords: ["P-Shot"],
    doctorSpecialization: "Men's Health",
    messages: [
      {
        text: "I saw you offer P-Shot treatment. Can you tell me more about it?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Of course! The P-Shot is a PRP treatment that enhances male performance and sensitivity. The procedure takes about 30 minutes and costs AED 2,500.",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "Is it painful? And how private is the consultation?",
        owner: "contact",
        delayMinutes: 13,
      },
      {
        text: "Local anesthesia is applied, so discomfort is minimal. All consultations with Dr. Omar Hassan are completely private and confidential. Would you like to schedule one?",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Yes, preferably evening time",
        owner: "contact",
        delayMinutes: 5,
      },
      {
        text: "We have Monday at 7 PM or Wednesday at 8 PM available for consultation with Dr. Omar.",
        owner: "user",
        delayMinutes: 1,
      },
      { text: "Wednesday at 8 PM please", owner: "contact", delayMinutes: 5 },
      {
        text: "Booked! Dr. Omar will see you Wednesday at 8:00 PM.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 6: Skin Booster Package
  {
    name: "Skin Booster Package",
    treatmentKeywords: ["Skin Booster"],
    doctorSpecialization: "Aesthetic",
    messages: [
      {
        text: "Hi! I'm interested in skin boosters. What packages do you have?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Hello! We offer skin boosters starting at AED 800 per session. For best results, we recommend a package of 3 sessions for AED 2,100, saving you AED 300!",
        owner: "user",
        delayMinutes: 6,
      },
      { text: "What areas can be treated?", owner: "contact", delayMinutes: 8 },
      {
        text: "Skin boosters work beautifully for face, neck, décolletage, and hands. They deeply hydrate and improve skin texture from within.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "I'd like to do the face package. Can I book?",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "Absolutely! When would you like your first session? We have availability Tuesday through Saturday.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Thursday morning if possible",
        owner: "contact",
        delayMinutes: 5,
      },
      {
        text: "Perfect! I've booked you for Thursday at 10:00 AM with Dr. Amira Al-Mansoori. Looking forward to seeing you!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 7: Dermapen + Mesotherapy Combo
  {
    name: "Dermapen Combo",
    treatmentKeywords: ["Dermapen", "Mesotherapy"],
    messages: [
      {
        text: "Do you offer Dermapen treatment?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes! Dermapen microneedling is AED 500 per session. We often combine it with Mesotherapy for enhanced results - the combo is AED 850.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "What's the benefit of combining them?",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "Dermapen creates micro-channels that allow Mesotherapy serums to penetrate deeper, maximizing collagen production and skin rejuvenation. Results are significantly better together.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "How many sessions would I need?",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "We typically recommend 4-6 sessions, spaced 4 weeks apart. Dr. Ravi Kumar can assess your skin and create a personalized plan during a consultation.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Okay, let me book a consultation",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Great! We have Tuesday at 3 PM or Friday at 11 AM. Which suits you?",
        owner: "user",
        delayMinutes: 1,
      },
      { text: "Friday at 11 AM", owner: "contact", delayMinutes: 4 },
      {
        text: "Booked! Dr. Ravi will see you Friday at 11:00 AM.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 8: Urgent Event Booking
  {
    name: "Emergency Pre-Event Treatment",
    treatmentKeywords: ["Filler", "Lip"],
    messages: [
      {
        text: "Hi! I have an event next week. Can I get lip fillers done before then?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes! Lip fillers can be done up to 3-4 days before an event to allow swelling to subside. When is your event?",
        owner: "user",
        delayMinutes: 3,
      },
      { text: "It's on Saturday evening", owner: "contact", delayMinutes: 5 },
      {
        text: "Perfect timing! I recommend booking for Monday or Tuesday. We have Monday at 4 PM available with Dr. Sophia Martinez, who specializes in lip enhancement.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Monday at 4 PM is perfect! How much is it?",
        owner: "contact",
        delayMinutes: 7,
      },
      {
        text: "Lip fillers start at AED 1,200 for 1mL. Dr. Sophia will assess and recommend the right amount during your appointment.",
        owner: "user",
        delayMinutes: 1,
      },
      {
        text: "Sounds good, let's book it!",
        owner: "contact",
        delayMinutes: 3,
      },
      {
        text: "Done! You're all set for Monday at 4:00 PM. Please arrive 10 minutes early to fill out consent forms.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 9: Rescheduling Request
  {
    name: "Reschedule Appointment",
    messages: [
      {
        text: "Hi, I need to reschedule my appointment tomorrow. Something came up at work",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "No problem! Let me check available slots. What treatment was scheduled?",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "It was for laser hair removal at 2 PM",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Got it! I can move you to Thursday at 2 PM or Friday at 11 AM. Which works better?",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Thursday at 2 PM is perfect, thank you!",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "All done! Your appointment has been rescheduled to Thursday at 2:00 PM. See you then!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 10: Insurance Inquiry
  {
    name: "Insurance Coverage",
    treatmentKeywords: ["Hair Restoration", "PRP"],
    messages: [
      {
        text: "Do you accept insurance for hair restoration treatments?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Aesthetic and cosmetic treatments like hair restoration are typically not covered by insurance as they're considered elective. However, we offer flexible payment plans!",
        owner: "user",
        delayMinutes: 6,
      },
      {
        text: "What kind of payment plans?",
        owner: "contact",
        delayMinutes: 9,
      },
      {
        text: "We work with Tabby and Tamara for installment plans. You can split payments into 3, 6, or 12 months with 0% interest for qualified amounts.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "That's helpful! Can I get a quote for PRP hair treatment?",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "PRP hair treatment is AED 800 per session. We recommend 4-6 sessions initially, then maintenance every 3-6 months. Would you like to book a consultation with Dr. James Anderson?",
        owner: "user",
        delayMinutes: 3,
      },
      { text: "Yes please", owner: "contact", delayMinutes: 8 },
      {
        text: "Great! We have Wednesday at 5 PM or Saturday at 11 AM. Which works for you?",
        owner: "user",
        delayMinutes: 1,
      },
      { text: "Wednesday at 5 PM", owner: "contact", delayMinutes: 5 },
      {
        text: "Booked! Dr. Anderson will see you Wednesday at 5:00 PM.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 11: Referral from Friend
  {
    name: "Friend Referral",
    treatmentKeywords: ["Botox"],
    messages: [
      {
        text: "Hi! My friend Sarah recommended your clinic. She had amazing results with Botox!",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "That's wonderful to hear! We love referrals 😊 Are you interested in Botox treatment as well?",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "Yes! She showed me her before and after photos. Can I see Dr. Amira too?",
        owner: "contact",
        delayMinutes: 7,
      },
      {
        text: "Of course! Dr. Amira Al-Mansoori is our lead aesthetic physician. She's available for consultations Tuesday through Saturday. When would you like to come in?",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "How about this Thursday?", owner: "contact", delayMinutes: 10 },
      {
        text: "Thursday works! We have 11 AM or 3 PM available.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "3 PM is better", owner: "contact", delayMinutes: 6 },
      {
        text: "Perfect! You're booked with Dr. Amira on Thursday at 3:00 PM. The consultation is complimentary!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 12: RF Slimming Course
  {
    name: "RF Slimming Package",
    treatmentKeywords: ["RF", "Slimming"],
    messages: [
      {
        text: "I'm interested in RF slimming for my abdomen. How does it work?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "RF slimming uses radiofrequency energy to heat deep layers of skin, promoting fat reduction and skin tightening. It's non-invasive with no downtime!",
        owner: "user",
        delayMinutes: 8,
      },
      {
        text: "How many sessions do I need to see results?",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "We recommend 6-8 sessions for optimal results. Each session is AED 500, or you can get a package of 6 sessions for AED 2,700, saving AED 300.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "That package sounds good. Can I book the first session?",
        owner: "contact",
        delayMinutes: 15,
      },
      {
        text: "Absolutely! Dr. Layla Al-Falasi specializes in body aesthetics. We have Monday at 6 PM or Wednesday at 10 AM available.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "Monday at 6 PM works", owner: "contact", delayMinutes: 7 },
      {
        text: "Booked! You're scheduled for Monday at 6:00 PM. Sessions are 45 minutes each. See you soon!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 13: Late Night Inquiry
  {
    name: "After Hours Message",
    treatmentKeywords: ["Consultation"],
    messages: [
      { text: "Hi, are you still open?", owner: "contact", delayMinutes: 0 },
      {
        text: "Good morning! Our clinic hours are 9 AM - 9 PM, Saturday to Thursday. How can we help you today?",
        owner: "user",
        delayMinutes: 620,
      }, // Next morning
      {
        text: "Oh okay! I wanted to ask about treatment options for acne scars",
        owner: "contact",
        delayMinutes: 15,
      },
      {
        text: "We offer several effective treatments for acne scars including Dermapen microneedling, Chemical Peels, and Laser resurfacing. Dr. Ravi Kumar specializes in skin concerns. Would you like to book a consultation?",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "Yes please! What's your earliest availability?",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "We have tomorrow at 12 PM or 4 PM available with Dr. Ravi.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Tomorrow at 4 PM is perfect",
        owner: "contact",
        delayMinutes: 5,
      },
      {
        text: "Great! You're booked for tomorrow at 4:00 PM. The consultation is complimentary and takes about 30 minutes.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 14: Package Deal Question
  {
    name: "Multi-Treatment Package",
    treatmentKeywords: ["Laser Hair Removal"],
    messages: [
      {
        text: "If I book multiple areas for laser hair removal, do you offer a discount?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes! We offer package deals. Which areas are you interested in?",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "Full legs, underarms, and bikini area",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "Great choice! Full legs are AED 600, underarms AED 150, and bikini AED 250 per session. As a package, we can offer all three areas for AED 900 per session instead of AED 1,000.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "That's a good deal! And how many sessions total?",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Typically 6-8 sessions spaced 4-6 weeks apart. If you book a course of 6 sessions upfront, it's AED 5,100 instead of AED 5,400 - additional savings!",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Perfect! I'd like to book the 6-session package",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "Excellent! Let me schedule your first session. We have availability this week on Wednesday at 1 PM or Friday at 3 PM with Dr. Priya Sharma.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "Friday at 3 PM", owner: "contact", delayMinutes: 6 },
      {
        text: "Booked! Your first session is Friday at 3:00 PM. We'll collect payment for the full package on your first visit. Excited for your journey!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 15: Treatment Compatibility
  {
    name: "Treatment Timing Question",
    treatmentKeywords: ["Botox", "Filler"],
    messages: [
      {
        text: "Can I get Botox and fillers done on the same day?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes absolutely! Many of our patients combine Botox and fillers in one appointment. It's actually very convenient and gives comprehensive facial rejuvenation.",
        owner: "user",
        delayMinutes: 6,
      },
      {
        text: "How long would the appointment take?",
        owner: "contact",
        delayMinutes: 9,
      },
      {
        text: "A combined session typically takes 45-60 minutes including consultation time. Dr. Sophia Martinez specializes in these combination treatments.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "What areas would you recommend for my first time?",
        owner: "contact",
        delayMinutes: 11,
      },
      {
        text: "Dr. Sophia will assess your face and recommend the best plan. Common combinations are Botox for forehead/crows feet with cheek or lip fillers. Book a consultation to discuss your goals!",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Sounds good! When can I see her?",
        owner: "contact",
        delayMinutes: 7,
      },
      {
        text: "She has Tuesday at 2 PM or Thursday at 5 PM available this week.",
        owner: "user",
        delayMinutes: 1,
      },
      { text: "Thursday at 5 PM please", owner: "contact", delayMinutes: 5 },
      {
        text: "Perfect! Dr. Sophia will see you Thursday at 5:00 PM. Looking forward to it!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 16: Aftercare Product Purchase
  {
    name: "Post-Treatment Products",
    messages: [
      {
        text: "Hi! I ran out of the moisturizer you gave me after my treatment. Can I buy more?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Of course! We have it in stock. It's AED 150 for the 50ml bottle. Would you like to pick it up or have it delivered?",
        owner: "user",
        delayMinutes: 8,
      },
      {
        text: "Can you deliver to Marina?",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "Yes! Delivery within Dubai is AED 25 and usually arrives within 2-3 hours during business hours. Shall I arrange it?",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Yes please! Can I pay on delivery?",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "Absolutely! Total is AED 175 (product + delivery). I'll send the driver with the card machine. Please share your exact address.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Marina Tower 3, Apartment 1205",
        owner: "contact",
        delayMinutes: 4,
      },
      {
        text: "Perfect! Your order will arrive within 2-3 hours. You'll receive a message when the driver is on the way.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 17: Gift Voucher
  {
    name: "Gift Voucher Inquiry",
    messages: [
      {
        text: "Do you sell gift vouchers? It's my wife's birthday next week",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes! We offer gift vouchers in any amount from AED 500 upwards. They're valid for 6 months and can be used for any treatment.",
        owner: "user",
        delayMinutes: 7,
      },
      {
        text: "Can I get a voucher for AED 1,500?",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "Absolutely! Would you like a physical voucher or digital one sent via email/WhatsApp?",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Physical one please. Can I collect it tomorrow?",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Yes! We can have it ready for pickup tomorrow. We'll present it in a beautiful gift envelope. When would you like to collect?",
        owner: "user",
        delayMinutes: 3,
      },
      { text: "Around 6 PM if possible", owner: "contact", delayMinutes: 6 },
      {
        text: "Perfect! Please pay online or when you collect. Sending payment link now. Collection is anytime after 6 PM tomorrow!",
        owner: "user",
        delayMinutes: 1,
      },
      {
        text: "Great, thanks! She'll love this",
        owner: "contact",
        delayMinutes: 5,
      },
    ],
  },

  // Scenario 18: Group Booking - Bridesmaids
  {
    name: "Bridal Party Booking",
    messages: [
      {
        text: "Hi! I'm getting married in 3 weeks. Can I bring 4 friends for treatments on the same day?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Congratulations! 🎉 Yes, we love bridal parties! What treatments are you interested in?",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "Thinking skin boosters or facials for everyone. What would you recommend?",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "For 3 weeks before wedding, I'd recommend Hydrafacial (AED 450) for instant glow with no downtime. Skin boosters need 1-2 weeks to settle. We can do back-to-back appointments for your group!",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "Perfect! Hydrafacial for 5 people then. Do you offer group discounts?",
        owner: "contact",
        delayMinutes: 15,
      },
      {
        text: "For groups of 5+, we offer 15% off! So AED 382 per person instead of AED 450. Total AED 1,910 for all five. Does that work?",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Amazing! Can we book for next Saturday morning?",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "Let me check... Yes! We can start at 9 AM and have everyone done by 12:30 PM. We'll have refreshments ready. Should I book?",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "Yes please! This is going to be so fun",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "Booked! 5 Hydrafacials next Saturday starting at 9:00 AM. Dr. Amira and the team will take care of you. We look forward to welcoming you all!",
        owner: "user",
        delayMinutes: 2,
      },
    ],
  },

  // Scenario 19: Beard Transplant Male Client
  {
    name: "Beard Transplant Inquiry",
    treatmentKeywords: ["Beard", "Transplant"],
    doctorSpecialization: "Hair",
    messages: [
      {
        text: "Do you do beard transplants? I have patchy growth",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes! Dr. James Anderson specializes in beard transplants using the FUE technique. Your consultation will cover what results to expect.",
        owner: "user",
        delayMinutes: 6,
      },
      { text: "How much does it cost?", owner: "contact", delayMinutes: 9 },
      {
        text: "Beard transplants start at AED 8,000 and go up to AED 15,000 depending on the number of grafts needed. Dr. James can give you an exact quote during consultation.",
        owner: "user",
        delayMinutes: 3,
      },
      { text: "What's the recovery like?", owner: "contact", delayMinutes: 14 },
      {
        text: "You'll see redness for 3-5 days. Most men return to work after 2-3 days. New hair grows in 3-4 months with full results at 12 months. No visible scarring with FUE method.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "That's not bad. Can I book a consultation?",
        owner: "contact",
        delayMinutes: 11,
      },
      {
        text: "Absolutely! Consultations are free. Dr. James has availability Wednesday at 7 PM or Sunday at 11 AM.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "Wednesday at 7 PM is good", owner: "contact", delayMinutes: 7 },
      {
        text: "Booked! Dr. James Anderson will see you Wednesday at 7:00 PM. He'll assess your beard area and create a customized plan.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 20: IV Therapy for Energy
  {
    name: "IV Therapy Wellness",
    treatmentKeywords: ["IV", "Therapy", "Drip"],
    messages: [
      {
        text: "I've been feeling really tired lately. Do you offer IV vitamin therapy?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Yes! We offer several IV therapy options. Our Energy Boost IV (Vitamin B complex + Vitamin C) is AED 400 and takes 30 minutes. Very popular!",
        owner: "user",
        delayMinutes: 7,
      },
      {
        text: "Is it safe? Will I feel better right away?",
        owner: "contact",
        delayMinutes: 11,
      },
      {
        text: "Completely safe! Our IVs are administered by trained nurses. Most people feel more energized within hours. Effects last 1-2 weeks depending on lifestyle.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "That sounds exactly what I need! Can I come in today?",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Let me check availability... Yes! We have a slot at 5 PM today with Dr. Layla Al-Falasi. Does that work?",
        owner: "user",
        delayMinutes: 3,
      },
      { text: "Perfect! See you at 5", owner: "contact", delayMinutes: 4 },
      {
        text: "Great! You're booked for 5:00 PM. Make sure to eat something light before coming and stay hydrated. See you soon!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 21: Jawline Filler Male
  {
    name: "Male Jawline Enhancement",
    treatmentKeywords: ["Filler", "Jaw"],
    messages: [
      {
        text: "I'm interested in jawline filler to make my face look more defined",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Great choice! Jawline contouring with fillers creates excellent definition. The treatment uses 2-4ml of filler depending on your desired result.",
        owner: "user",
        delayMinutes: 8,
      },
      { text: "How much would 3ml cost?", owner: "contact", delayMinutes: 10 },
      {
        text: "3ml of premium dermal filler for jawline is AED 2,400. This includes both jawline and chin if needed for optimal proportions. Dr. Omar Hassan specializes in male aesthetics.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "Will it look natural? I don't want it to be obvious",
        owner: "contact",
        delayMinutes: 13,
      },
      {
        text: "Absolutely! Dr. Omar's approach for men is very natural and masculine. The goal is enhancement, not drastic change. Results last 12-18 months.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Okay I'm convinced. When can I schedule?",
        owner: "contact",
        delayMinutes: 9,
      },
      {
        text: "Dr. Omar has Tuesday at 6 PM or Saturday at 2 PM available. Which works better for you?",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "Saturday at 2 PM", owner: "contact", delayMinutes: 6 },
      {
        text: "Booked! Dr. Omar will see you Saturday at 2:00 PM. The procedure takes 45 minutes. Looking forward to your transformation!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 22: Chemical Peel Series
  {
    name: "Chemical Peel Treatment",
    treatmentKeywords: ["Chemical Peel", "Peel"],
    messages: [
      {
        text: "I have pigmentation issues. Would chemical peels help?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Chemical peels are excellent for pigmentation! We offer different strengths depending on your skin concern. Dr. Ravi Kumar can assess which type is best for you.",
        owner: "user",
        delayMinutes: 9,
      },
      {
        text: "What's the difference between the types?",
        owner: "contact",
        delayMinutes: 12,
      },
      {
        text: "Light peels (AED 350) have minimal downtime, done monthly. Medium peels (AED 600) penetrate deeper with 3-5 days peeling, better for stubborn pigmentation. Dr. Ravi will recommend based on your skin.",
        owner: "user",
        delayMinutes: 5,
      },
      {
        text: "How many sessions would I need?",
        owner: "contact",
        delayMinutes: 15,
      },
      {
        text: "Typically 4-6 light peels or 3 medium peels spaced 6 weeks apart. Results are progressive. We also offer a package of 4 light peels for AED 1,200, saving AED 200.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "Let me start with a consultation to see what he recommends",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "Smart approach! Consultations are complimentary. Dr. Ravi has Monday at 11 AM or Thursday at 4 PM available.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "Monday at 11 AM works", owner: "contact", delayMinutes: 7 },
      {
        text: "Perfect! Dr. Ravi will see you Monday at 11:00 AM. He'll examine your skin and create a customized treatment plan.",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 23: Price Shopping Multiple Clinics
  {
    name: "Price Comparison",
    treatmentKeywords: ["Laser Hair Removal"],
    messages: [
      {
        text: "I'm comparing prices for laser hair removal. What do you charge for full legs?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Our full legs laser hair removal is AED 600 per session. We use the latest Soprano ICE technology which is virtually painless and works on all skin types.",
        owner: "user",
        delayMinutes: 6,
      },
      {
        text: "That's higher than some other places I checked",
        owner: "contact",
        delayMinutes: 14,
      },
      {
        text: "Quality makes a difference! We use premium technology, our doctors are UK-trained, and we guarantee results. Many cheaper clinics use older lasers that are painful and less effective. We also offer package discounts - 6 sessions for AED 3,300 instead of AED 3,600.",
        owner: "user",
        delayMinutes: 5,
      },
      { text: "What if it doesn't work?", owner: "contact", delayMinutes: 11 },
      {
        text: "We offer a satisfaction guarantee. If you complete the recommended course and don't see at least 80% hair reduction, we'll provide additional sessions free. Plus free consultation with Dr. Priya to assess your hair type first.",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "Okay that makes me feel better about the price. Can I book the consultation?",
        owner: "contact",
        delayMinutes: 16,
      },
      {
        text: "Absolutely! Dr. Priya Sharma can see you this week. We have Wednesday at 3 PM or Friday at 1 PM.",
        owner: "user",
        delayMinutes: 2,
      },
      { text: "Friday at 1 PM", owner: "contact", delayMinutes: 8 },
      {
        text: "Booked! Dr. Priya will see you Friday at 1:00 PM. She'll do a patch test and explain exactly how it works. See you then!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 24: PRP for Hair Loss Female
  {
    name: "Female Hair Loss PRP",
    treatmentKeywords: ["PRP", "Hair"],
    messages: [
      {
        text: "I'm experiencing hair thinning. Can PRP help women too?",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "Absolutely! PRP is highly effective for female pattern hair loss. It stimulates hair follicles using your own growth factors. Many of our female patients see great results.",
        owner: "user",
        delayMinutes: 7,
      },
      { text: "Is it painful?", owner: "contact", delayMinutes: 9 },
      {
        text: "Minimal discomfort! We apply numbing cream before the injections. Most patients describe it as tiny pinpricks. The whole procedure takes 45 minutes.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "How much does it cost and how many sessions?",
        owner: "contact",
        delayMinutes: 11,
      },
      {
        text: "PRP hair treatment is AED 800 per session. We recommend 4 sessions monthly, then maintenance every 3-6 months. Package of 4 sessions is AED 2,900, saving AED 300.",
        owner: "user",
        delayMinutes: 4,
      },
      { text: "When will I see results?", owner: "contact", delayMinutes: 13 },
      {
        text: "Most people notice reduced shedding after 2 sessions, new hair growth after 3-4 months. Best results at 6 months. Dr. James Anderson will track your progress with photos.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "I'd like to start. When is the earliest I can come?",
        owner: "contact",
        delayMinutes: 10,
      },
      {
        text: "Dr. James has availability tomorrow at 4 PM or Tuesday at 11 AM.",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Tomorrow at 4 PM is perfect",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "Wonderful! You're booked for tomorrow at 4:00 PM with Dr. James. Please wash your hair that morning but don't use any styling products. Excited to start your journey!",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },

  // Scenario 25: General Location/Parking Question
  {
    name: "Clinic Location Query",
    messages: [
      {
        text: "Where exactly is your clinic located? I have an appointment tomorrow",
        owner: "contact",
        delayMinutes: 0,
      },
      {
        text: "We're in Jumeirah Lake Towers, Cluster V, Fortune Tower, Ground Floor. Very easy to find! Are you driving or using metro?",
        owner: "user",
        delayMinutes: 4,
      },
      {
        text: "I'm driving. Is parking available?",
        owner: "contact",
        delayMinutes: 8,
      },
      {
        text: "Yes! There's free parking in the building's basement. Enter from Sheikh Zayed Road service road, parking is P1 level. We also validate parking tickets.",
        owner: "user",
        delayMinutes: 3,
      },
      {
        text: "Perfect! What time should I arrive for a 3 PM appointment?",
        owner: "contact",
        delayMinutes: 6,
      },
      {
        text: "Please arrive 10 minutes early (2:50 PM) to complete any paperwork. The reception is right as you enter from the parking. You'll see our ACME Clinic sign!",
        owner: "user",
        delayMinutes: 2,
      },
      {
        text: "Great, thanks! See you tomorrow",
        owner: "contact",
        delayMinutes: 4,
      },
      {
        text: "See you tomorrow! Safe drive, and feel free to message if you need any navigation help 😊",
        owner: "user",
        delayMinutes: 1,
      },
    ],
  },
  // Scenario 26: Quoted replies showcase - exercises all four reply directions
  // (contact→contact, contact→agent, agent→contact, agent→agent) via the `linked` field.
  {
    name: "Quoted Replies Showcase",
    treatmentKeywords: ["Laser Hair Removal", "Full Legs"],
    doctorSpecialization: "Laser",
    messages: [
      // 0 - contact opens
      {
        text: "Hi! I wanted to ask about your full legs laser hair removal packages.",
        owner: "contact",
        delayMinutes: 0,
      },
      // 1 - agent answers
      {
        text: "Hi there! Full legs is AED 600 per session, and we recommend 6 to 8 sessions for the best results.",
        owner: "user",
        delayMinutes: 3,
      },
      // 2 - contact replies to their OWN first message (contact → contact)
      {
        text: "Adding to this: I'd also like to include underarms in the same plan.",
        owner: "contact",
        delayMinutes: 5,
        replyToIndex: 0,
      },
      // 3 - contact replies to the AGENT's message (contact → agent)
      {
        text: "Does the AED 600 per session already include the consultation?",
        owner: "contact",
        delayMinutes: 2,
        replyToIndex: 1,
      },
      // 4 - agent replies to the CONTACT's question (agent → contact)
      {
        text: "Yes, the initial consultation is complimentary with any package.",
        owner: "user",
        delayMinutes: 4,
        replyToIndex: 3,
      },
      // 5 - agent replies to their OWN earlier pricing message (agent → agent)
      {
        text: "To add to my pricing note above: underarms is AED 150 per session, so we can bundle both.",
        owner: "user",
        delayMinutes: 1,
        replyToIndex: 1,
      },
      // 6 - contact wraps up
      {
        text: "Perfect, that works for me. Let's go ahead and book it!",
        owner: "contact",
        delayMinutes: 6,
      },
    ],
  },
];

interface GenerateConversationsParams {
  contacts: Contact[];
  users: User[];
  channels: Channel[];
  doctors: Doctor[];
  treatments: Treatment[];
  closingNotes: ClosingNote[];
  organizationId: string;
}

interface GenerateConversationsResult {
  conversations: Map<string, Conversation>;
  messages: Map<string, Message[]>;
  activities: Map<string, Activity[]>;
}

/**
 * Find best matching doctor for a conversation flow
 */
function findMatchingDoctor(flow: ConversationFlow, doctors: Doctor[]): Doctor {
  if (flow.doctorSpecialization) {
    const matching = doctors.find((d) =>
      d.name.toLowerCase().includes(flow.doctorSpecialization!.toLowerCase()),
    );

    if (matching) return matching;
  }

  return pickRandom(doctors);
}

/**
 * Replace placeholders in message text with actual doctor names
 */
function replacePlaceholders(text: string, doctor: Doctor): string {
  return text.replace(
    /Dr\.\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+(?:-[A-Z][a-z]+)?)?/g,
    doctor.name,
  );
}

export function buildPendingAttachment(withSourceUrl: boolean): Attachment {
  const now = new Date();

  return {
    id: createId(),
    url: withSourceUrl ? "https://picsum.photos/seed/pending/600/800" : "",
    name: null,
    caption: null,
    externalId: createId(),
    type: "image",
    createdAt: now,
    updatedAt: now,
    contentType: "image/jpeg",
    source: "conversation",
    status: "pending",
  };
}

function generateMessages(
  _contactId: string,
  channel: Channel,
  users: User[],
  doctors: Doctor[],
  organizationId: string,
  flow: ConversationFlow,
): Message[] {
  const messages: Message[] = [];
  const doctor = findMatchingDoctor(flow, doctors);
  const assignedUser = pickRandom(users);

  // Calculate start time - work backwards from now
  const now = new Date();
  const totalMinutes = flow.messages.reduce(
    (sum, msg) => sum + msg.delayMinutes,
    0,
  );
  let currentTime = new Date(now.getTime() - totalMinutes * 60000);

  for (let i = 0; i < flow.messages.length; i++) {
    const flowMessage = flow.messages[i];

    // Add delay from this message
    currentTime = new Date(
      currentTime.getTime() + flowMessage.delayMinutes * 60000,
    );

    const messageId = createId();
    const isFromContact = flowMessage.owner === "contact";
    const status: MessageStatus = isFromContact ? "received" : "delivered";

    // Showcase a still-processing (pending) attachment on the first inbound
    // message: an empty-url WhatsApp media awaiting re-hosting.
    const pendingAttachments: Attachment[] =
      i === 0 && isFromContact ? [buildPendingAttachment(false)] : [];
    const messageType = pendingAttachments.length > 0 ? "attachment" : "text";

    // Replace any doctor placeholders in the text
    const text = replacePlaceholders(flowMessage.text, doctor);

    // Resolve the quoted/replied-to message from a prior message in this flow.
    // The nested copy drops its own `linked` to avoid deep recursion.
    const repliedTo =
      flowMessage.replyToIndex !== undefined &&
      flowMessage.replyToIndex >= 0 &&
      flowMessage.replyToIndex < messages.length
        ? messages[flowMessage.replyToIndex]
        : null;
    const linked: Message | null = repliedTo
      ? { ...repliedTo, linked: null }
      : null;

    messages.push({
      id: messageId,
      text,
      owner: flowMessage.owner,
      externalId: createId(),
      organizationId,
      createdAt: currentTime.toISOString(),
      updatedAt: currentTime.toISOString(),
      receivedAt: currentTime.toISOString(),
      statuses: [
        {
          id: createId(),
          status,
          createdAt: currentTime,
          updatedAt: currentTime,
          receivedAt: currentTime,
          messageId,
        },
      ],
      attachments: pendingAttachments,
      reactions: [],
      user: isFromContact
        ? null
        : {
            ...assignedUser,
            createdAt: assignedUser.createdAt,
            updatedAt: assignedUser.updatedAt,
          },
      type: messageType,
      channel: { id: channel.id },
      linked,
    });
  }

  // Hang an agent run off the newest inbound message - the same anchor the real
  // history load uses, and what `useShowsInboxAgentTimeline` looks for. Without
  // it the inbox trace is unreachable in mock mode (the inbox never fetches
  // steps, it only reads `message.steps`), so the surface can't be screenshotted
  // or covered by e2e at all. Steps are derived from the message id, so a given
  // conversation always renders the same run.
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].owner === "contact") {
      messages[i].steps = makeAgentRunSteps(messages[i].id, "settled");
      break;
    }
  }

  return messages;
}

const AI_REPLY_SHOWCASE: string[][] = [
  [
    "Hi Priya! Yes, we do laser hair removal for the upper lip and chin.",
    "A single session is ₹2,500 and most people need 6 to 8 sessions, spaced about a month apart.",
    "Would you like me to check a slot for a quick patch test this week?",
  ],
  [
    "Good news, your results from Tuesday are in and everything looks normal.",
    "Dr. Rao suggested a follow-up in three months. Shall I pencil one in?",
  ],
];

/**
 * An AI reply that already went out: a run of `owner: "ai"` bubbles sharing a
 * generation id, the way the backend stamps them, so the thread's reply review
 * control has something to attach to and a ledger row to read.
 */
function appendAiReply(
  messages: Message[],
  texts: string[],
  channel: Channel,
  organizationId: string,
  generationId: string,
): void {
  const last = messages[messages.length - 1];
  const base = last ? new Date(last.createdAt).getTime() : Date.now();

  texts.forEach((text, index) => {
    const at = new Date(base + (index + 1) * 20_000);
    const messageId = createId();

    messages.push({
      id: messageId,
      text,
      owner: "ai",
      externalId: createId(),
      organizationId,
      createdAt: at.toISOString(),
      updatedAt: at.toISOString(),
      receivedAt: at.toISOString(),
      statuses: [
        {
          id: createId(),
          status: "delivered",
          createdAt: at,
          updatedAt: at,
          receivedAt: at,
          messageId,
        },
      ],
      attachments: [],
      reactions: [],
      user: null,
      type: "text",
      channel: { id: channel.id },
      linked: null,
      meta: { generationId },
    });
  });
  seedMockReplyReview(
    generationId,
    texts.map((text) => ({ type: "text", text })),
  );
}

const CALL_OUTCOMES: CallOutcome[] = [
  "answered",
  "missed",
  "voicemail",
  "busy",
  "no_answer",
];
const CALL_DIRECTIONS: CallDirection[] = ["inbound", "outbound"];
const CALL_NOTES: string[] = [
  "Customer inquired about pricing for laser hair removal. Quoted AED 600 per session for full legs. Scheduled follow-up consultation for Tuesday.",
  "Discussed treatment options for acne scarring. Recommended Dermapen microneedling series. Customer will think about it and call back.",
  "Follow-up call regarding post-treatment care. Patient experiencing normal mild redness after Carbon Laser. Advised to continue using prescribed moisturizer.",
  "Confirmed appointment for Thursday at 3 PM with Dr. Amira. Customer requested information about payment plans.",
  "Rescheduled appointment from Monday to Wednesday due to work conflict. New time confirmed at 2:00 PM.",
  "Customer asked about Botox for forehead lines. Explained package options - single area vs 3-area combo. Very interested, will book online.",
  "Inquiry about gift vouchers for wife's birthday. Requested AED 1,500 voucher. Will collect tomorrow at 6 PM.",
  "Post-procedure check-in call. Patient very happy with PRP hair treatment results after 3 sessions. Booked maintenance session.",
];

// Sample audio URL
const SAMPLE_RECORDING_URL =
  "https://cdn.freesound.org/previews/842/842606_17604172-lq.mp3";

// Featured call note for first conversation (2-3 lines)
const FEATURED_CALL_NOTE =
  "Customer called to inquire about laser hair removal pricing and availability. Discussed full leg treatment at AED 600 per session with 6-8 sessions recommended. Scheduled consultation for Tuesday at 2 PM with Dr. Priya Sharma.";

// Free-text contact notes left by agents - shown in the Contact Notes panel.
const CONTACT_NOTES: string[] = [
  "Prefers afternoon appointments. Sensitive skin, so recommend a patch test before any laser session.",
  "Allergic to lidocaine; flag for the doctor before any numbing cream is applied.",
  "Keen on the 6-session laser package but waiting on her partner's schedule. Follow up next week.",
  "VIP referral from Dr. Amira. Offer a complimentary consultation on the first visit.",
  "Needs the invoice issued under her company name for insurance reimbursement.",
  "Prefers WhatsApp over phone calls for follow-ups outside business hours.",
];

export const DEMO_AI_SESSION_ID = "ses-demo-1";

function generateActivities(
  contactId: string,
  users: User[],
  lifecycleId: string,
  closingNotes: ClosingNote[],
  isFirstContact: boolean = false,
): Activity[] {
  const activities: Activity[] = [];
  const user = pickRandom(users);

  // Created activity
  activities.push({
    id: createId(),
    userId: user.id,
    contactId,
    mentioned: [],
    createdAt: createPastTimestamp(30),
    type: "created",
    payload: {
      userId: user.id,
      description: "Created from an inbound conversation.",
    },
  });

  // Lifecycle change activity
  activities.push({
    id: createId(),
    userId: user.id,
    contactId,
    mentioned: [],
    createdAt: createPastTimestamp(20),
    type: "lifecycle_change",
    payload: { lifecycleId },
  });

  // Maybe add an assignment activity
  if (randomBool()) {
    activities.push({
      id: createId(),
      userId: user.id,
      contactId,
      mentioned: [],
      createdAt: createPastTimestamp(15),
      type: "assigned",
      payload: { assignee: user.id, reason: "manual" },
    });
  }

  // The first contact covers the ownership shapes the inbox must tell apart: a
  // routine system assignment (collapses), and - each earning a card - the AI's
  // own handoff reason, a human transfer note, and a noted unassignment.
  if (isFirstContact) {
    const other = users.find(({ id }) => id !== user.id) ?? user;

    activities.push(
      {
        id: createId(),
        userId: null,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(14),
        owner: "system",
        type: "assigned",
        payload: {
          assignee: other.id,
          previousAssignee: user.id,
          reason: "availability",
        },
      },
      // Routing handing the conversation to the AI: modelled as "no human
      // assignee", which used to render as a release that never happened.
      {
        id: createId(),
        userId: null,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(13.5),
        owner: "system",
        type: "unassigned",
        payload: {
          assignee: null,
          toAi: true,
          previousAssignee: other.id,
          reason: "availability",
        },
      },
      // A teammate pressing Take over: the AI leaves, they hold it.
      {
        id: createId(),
        userId: other.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(13.25),
        type: "assigned",
        payload: {
          assignee: other.id,
          fromAi: true,
          reason: "takeover",
        },
      },
      {
        id: createId(),
        userId: null,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(13),
        owner: "ai",
        type: "assigned",
        payload: {
          assignee: user.id,
          reason: "ai_handoff",
          description:
            "She's disputing the charge from her last session and asked for a refund. I can't authorise that.",
        },
      },
      {
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(12),
        type: "assigned",
        payload: {
          assignee: other.id,
          reason: "manual",
          description:
            "Handing over. You treated her in March so you'll have the context. She's expecting a call back today.",
        },
      },
      {
        id: createId(),
        userId: other.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(11),
        type: "unassigned",
        payload: {
          assignee: "",
          reason: "manual",
          description:
            "Dropping this back to the pool. I'm on leave from tomorrow and she needs an answer this week.",
        },
      },
    );
  }

  // A teammate-started Echo session that ended in a sent reply.
  if (isFirstContact) {
    activities.push({
      id: createId(),
      userId: user.id,
      contactId,
      mentioned: [],
      createdAt: createPastTimestamp(1),
      owner: "ai",
      type: "ai_session",
      payload: {
        sessionId: DEMO_AI_SESSION_ID,
        outcome: "sent",
        runs: 2,
        generationIds: [
          `${DEMO_AI_SESSION_ID}-gen-1`,
          `${DEMO_AI_SESSION_ID}-gen-2`,
        ],
        actions: ["Checked availability", "Booked Dr. Rai at 3:00 PM"],
        durationMs: 14_200,
      },
    });
  }

  // The first contact showcases a website form submission followed by a call
  // with audio, summary, and transcription.
  if (isFirstContact) {
    activities.push({
      id: createId(),
      userId: null,
      contactId,
      mentioned: [],
      createdAt: createPastTimestamp(6),
      owner: "api",
      type: "form_submitted",
      payload: {
        submissionId: createId(),
        formId: createId(),
        formName: "Laser Hair Removal Consultation",
        source: "website",
        fields: [
          { question: "Treatment of interest", value: "Full legs laser" },
          { question: "Preferred time", value: "Afternoon" },
          { question: "First treatment?", value: "Yes" },
        ],
        websiteVisit: {
          url: "https://acme.clinic/treatments/laser-hair-removal",
          pagePath: "/treatments/laser-hair-removal",
          pageTitle: "Laser Hair Removal Dubai | ACME Clinic",
          utmSource: "instagram",
          utmMedium: "paid-social",
          utmCampaign: "summer-smooth",
          utmTerm: null,
          utmContent: "full-legs-consultation",
        },
        submittedAt: createPastTimestamp(6),
      },
    });

    activities.push({
      id: createId(),
      userId: user.id,
      contactId,
      mentioned: [],
      createdAt: createPastTimestamp(5),
      type: "call",
      payload: {
        duration: 245, // ~4 minutes
        outcome: "answered" as CallOutcome,
        callerId: createPhoneNumber(),
        direction: "inbound" as CallDirection,
        recordingUrl: SAMPLE_RECORDING_URL,
        notes: FEATURED_CALL_NOTE,
        transcription: CALL_TRANSCRIPTION,
      },
    });
  } else if (randomBool(0.4)) {
    // Maybe add call activities (40% chance) for other contacts
    const callCount = randomInt(1, 3);

    for (let i = 0; i < callCount; i++) {
      const outcome = pickRandom(CALL_OUTCOMES);
      const direction = pickRandom(CALL_DIRECTIONS);

      // Duration only relevant for answered calls (30s to 15min)
      const duration = outcome === "answered" ? randomInt(30, 900) : 0;

      // Generate a realistic caller ID (UAE phone format)
      const callerId = createPhoneNumber();

      // All answered calls get recording and summary
      const recordingUrl =
        outcome === "answered" ? SAMPLE_RECORDING_URL : undefined;
      const notes = outcome === "answered" ? pickRandom(CALL_NOTES) : undefined;

      activities.push({
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(10 - i * 2),
        type: "call",
        payload: {
          duration,
          outcome,
          callerId,
          direction,
          recordingUrl,
          notes,
        },
      });
    }
  }

  // Non-first contacts get a richer history so the conversation Activities tab
  // showcases the full timeline: actor + system events, and the secondary-detail
  // chips (tag, follow-up time/reason, appointment change, closing category +
  // summary). The first contact is left as the form-to-phone-call showcase.
  if (!isFirstContact) {
    const closingCategory =
      closingNotes.find((note) => note.category === "Not Interested") ??
      closingNotes[0];
    const reasonField = closingCategory?.fields.find(
      (field) => field.type === "select",
    );

    activities.push(
      {
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(4),
        type: "tag_added",
        payload: { tags: [{ id: createId(), name: "vip-customer" }] },
      },
      {
        id: createId(),
        userId: null,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(3),
        owner: "system",
        type: "follow_up_set",
        payload: {
          followUpAt: createPastTimestamp(1),
          followUpReason: "Idle for so long, check with the user",
          source: "system",
        },
      },
      {
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(3),
        type: "appointment_updated",
        payload: {
          appointmentId: createId(),
          treatmentName: "Slimming Therapy",
          doctorName: "Dr Gezel Khalil",
          bookingType: "service",
          visitType: "first_time",
          startDate: "2026-08-07T10:45:00.000Z",
          endDate: "2026-08-07T11:15:00.000Z",
          status: "confirmed",
          changes: [
            { field: "status_update", previous: "pending", new: "confirmed" },
          ],
        },
      },
      {
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(3),
        type: "order_update",
        payload: {
          orderId: createId(),
          displayOrderId: "RPL-1042",
          target: "payment",
          oldStatus: "pending",
          newStatus: "paid",
          createdAt: createPastTimestamp(4),
        },
      },
      {
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(2),
        type: "lifecycle_change",
        payload: { lifecycleId, reason: "Qualified after product demo" },
      },
      {
        id: createId(),
        userId: null,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(2),
        owner: "system",
        type: "workflow_ended",
        payload: {
          workflowId: createId(),
          workflowName: "Idle Conversation",
          executionId: createId(),
          reason: "failed",
          exitReason: "No receiver specified for SEND_MESSAGE",
        },
      },
      {
        id: createId(),
        userId: user.id,
        contactId,
        mentioned: [],
        createdAt: createPastTimestamp(1),
        type: "conversation_status_change",
        payload: {
          status: "closed",
          closingNote: {
            topicId: closingCategory?.id ?? createId(),
            topicName: closingCategory?.category ?? "Not Interested",
            summary:
              "The customer is interested, but the timing is not right. Reconnect next quarter.",
            fieldValues: reasonField
              ? [
                  {
                    fieldId: reasonField.id,
                    fieldName: reasonField.name,
                    type: reasonField.type,
                    value: { selected: "Timing" },
                  },
                ]
              : undefined,
          },
        },
      },
    );
  }

  // Contact notes - surfaced in the Contact Notes panel (and the Activities tab).
  // The first contact always gets a couple so the notes UI shows by default.
  const noteCount = isFirstContact ? 2 : randomInt(1, 2);
  const noteTexts = pickMultiple(CONTACT_NOTES, noteCount, noteCount);

  for (let i = 0; i < noteTexts.length; i++) {
    activities.push({
      id: createId(),
      userId: (i === 0 ? users[0] : pickRandom(users)).id,
      contactId,
      mentioned: [],
      createdAt: createPastTimestamp(i + 1),
      owner: "user",
      type: "note",
      payload: { text: noteTexts[i] },
    });
  }

  return activities;
}

/**
 * Generate conversations with complete pre-defined flows
 * Each contact gets one complete conversation scenario
 */
export function generateConversations(
  params: GenerateConversationsParams,
): GenerateConversationsResult {
  const { contacts, users, channels, doctors, closingNotes, organizationId } =
    params;

  const conversations = new Map<string, Conversation>();
  const messages = new Map<string, Message[]>();
  const activities = new Map<string, Activity[]>();

  for (let i = 0; i < contacts.length; i++) {
    const contact = contacts[i];
    const isFirstContact = i === 0;

    // Anchor the conversation to a channel the contact is actually connected to
    // (matched by id), so the composer auto-selects an in-window channel rather
    // than an unrelated org channel that would read as out-of-window.
    const channel =
      contact.channels.length > 0
        ? (channels.find((c) => c.id === contact.channels[0].id) ?? channels[0])
        : channels[0];

    // Generate activities first so we can use call info
    const contactActivities = generateActivities(
      contact.id,
      users,
      contact.lifecycleId || "",
      closingNotes,
      isFirstContact,
    );

    activities.set(contact.id, contactActivities);

    const latestNote = contactActivities
      .filter((activity) => activity.type === "note")
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

    contact.lastNote = latestNote?.payload.text ?? null;
    contact.lastNoteAt = latestNote?.createdAt ?? null;
    if (contact.followUp?.reason) {
      contact.followUp.reason =
        "Confirm the patient's preferred appointment time.";
    }

    // Find the most recent call activity if any
    const callActivity = contactActivities
      .filter((a) => a.type === "call")
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0] as (Activity & { type: "call" }) | undefined;

    // First contact is a phone call - no chat messages, only call activity with transcription
    if (isFirstContact) {
      contact.lastSummary = callActivity?.payload.notes ?? null;
      // No messages for phone calls - transcription is in the call activity
      messages.set(contact.id, []);

      // Create conversation without a message but with call activity info
      conversations.set(contact.id, {
        contact,
        message: undefined,
        call: callActivity
          ? {
              id: callActivity.id,
              createdAt: callActivity.createdAt,
              payload: callActivity.payload,
            }
          : undefined,
      });
    } else {
      // Second contact always shows the quoted-replies showcase so all four
      // reply directions are exercised; the rest get a random flow.
      const flow =
        i === 1
          ? (CONVERSATION_FLOWS.find(
              (f) => f.name === "Quoted Replies Showcase",
            ) ?? pickRandom(CONVERSATION_FLOWS))
          : pickRandom(CONVERSATION_FLOWS);

      // Generate messages from the complete flow
      const contactMessages = generateMessages(
        contact.id,
        channel,
        users,
        doctors,
        organizationId,
        flow,
      );

      // The third and fourth contacts end on an AI reply, so the thread's reply
      // review control and the draft panel have something to show in mock mode.
      if (i === 2 || i === 3) {
        appendAiReply(
          contactMessages,
          AI_REPLY_SHOWCASE[i - 2],
          channel,
          organizationId,
          `gen-mock-reply-${contact.id}`,
        );
      }

      messages.set(contact.id, contactMessages);

      // Create conversation with the last message and call activity if any
      const lastMessage = contactMessages[contactMessages.length - 1];

      contact.lastSummary = lastMessage?.text ?? null;

      conversations.set(contact.id, {
        contact,
        message: lastMessage,
        call: callActivity
          ? {
              id: callActivity.id,
              createdAt: callActivity.createdAt,
              payload: callActivity.payload,
            }
          : undefined,
      });
    }
  }

  return { conversations, messages, activities };
}
